/* eslint-disable react/display-name */
import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react'

import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { listTestimonials } from '../../_actions'
import { useWindowSize } from '../../_helpers'
import {
    devices,
    colors,
    Title,
} from '../Common'

const TestimonialBlock = forwardRef(({ index, t }, ref = null) => (
    <Testimonial color={(index + 1) % 2 === 0 ? colors.red : colors.black} ref={ref}>
        {index === 0 && <PageTitle fontSize={26}>Testimonials</PageTitle>}
        <Text>
            {t.image && <img src={t.image} />}
            <Content>{`"${t.content}"`}</Content>
            <NameAndTitle color={(index + 1) % 2 === 0 ? colors.black : colors.red}>{t.name} / {t.title}</NameAndTitle>
        </Text>
    </Testimonial>
))

const TestimonialList = ({ list }) => list.map((t, i) => <TestimonialBlock key={t.name} index={i} t={t} />)

const TestimonialCarousel = ({ list }) => {
    const [activeSlide, setActiveSlide] = useState(0)
    const [visibleSlide, setVisibleSlide] = useState(0)
    const containerRef = useRef(null)
    const activeSlideRef = useRef(null)

    const scrollToActiveSlide = useCallback(() => {
        const activeSlide = activeSlideRef.current
        const wrapper = containerRef.current
        if (wrapper && activeSlide) {
            wrapper.scrollTo({
                top: activeSlide.offsetTop,
                left: 0,
                behavior: 'smooth'
            })
        }
    }, [])

    const handleScroll = useCallback(() => {
        let { height } = containerRef.current.getBoundingClientRect()
        let { scrollTop } = containerRef.current

        setVisibleSlide(Math.round(scrollTop / height))
    }, [])

    useEffect(scrollToActiveSlide, [activeSlide])

    return (
        <>
            <CarouselContainer ref={containerRef} onScroll={handleScroll}>
                {list.map((t, i) => <TestimonialBlock key={t.name} index={i} ref={i === activeSlide ? activeSlideRef : null} t={t} />)}
            </CarouselContainer>
            <Dots>
                {list.map((t, i) =>
                    <Dot key={i} role='button' onClick={() => setActiveSlide(i)} active={i === visibleSlide} aria-label={`Testimonial ${i}`} />
                )}
                <Count>{visibleSlide + 1} / {list.length}</Count>
            </Dots>
        </>
    )
}

const Testimonials = () => {
    const { testimonials } = useSelector(state => state.content, shallowEqual)
    const dispatch = useDispatch()
    const { width } = useWindowSize()
    const mobile = width < 940

    useEffect(() => {
        dispatch(listTestimonials())
    }, [])

    return mobile ? <TestimonialList list={testimonials} /> : <TestimonialCarousel list={testimonials} />
}

const CarouselContainer = styled.div`
    position: relative;
    height: 100vh;
    overflow-y: auto;

    /* hide scrollbar */
    -ms-overflow-style: none;
    scrollbar-width: none; 

    &::-webkit-scrollbar {
        display: none;
    }
`

const Text = styled.div`
    width: 95%;

    @media ${devices.small} {
        width: 100%;
    }
`

const PageTitle = styled(Title)`
    position: absolute;
    top: 50px;
    @media ${devices.medium} {
        top: 30px;
    }
`

const Testimonial = styled.div`
    background: ${({ color }) => color};
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 100px;

    img {
        width: 150px !important;
        height: 150px;
        border-radius: 50%;
    }
    
    @media ${devices.small} {
        position: relative;
        padding: 30px 20px;
        align-items: flex-start;
        justify-content: center;
        flex-direction: column;
        /* height: 87vh; */
        height: calc(95vh - 60px);
        
        img {
            width: 80px !important;
            height: 80px;
            margin-bottom: 40px;
        }
    }
`

const Content = styled.div`
    color: ${colors.white};
    font-size: 30px;
    font-family: pragmatica, sans-serif;
    font-weight: 700;
    font-style: normal;
    text-align: left;
    margin-bottom: 50px;

    @media ${devices.small} {
        font-size: 18px;
    }

    @media ${devices.large} {
        font-size: 23px;
    }
`

const NameAndTitle = styled.div`
    text-transform: uppercase;
    color: ${({ color }) => color};
    font-size: 15px;
    font-family: pragmatica, sans-serif;
    font-weight: 700;
    font-style: normal;
    text-align: left;
    letter-spacing: 2px;

    @media ${devices.small} {
        font-size: 8px;
        line-height: 1.5;
    }
`

const Dots = styled.div`
    margin-top: 20px;
    text-align: center;
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
    
    position: absolute;
    top: 20%;
    right: 25px;

    @media ${devices.small} {
        display: none;
    }

    @media ${devices.large} {
        top: 15%;
    }
`

const Dot = styled.div`
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 16px;
    margin-bottom: 17px;
    background: ${colors.white};
    transition: opacity 0.3s ease;
    opacity: ${({ active }) => active ? 1 : 0.5};
    cursor: pointer;
`

const Count = styled.div`
    color: ${colors.white};
    font-family: pragmatica, sans-serif;
    font-weight: 700;
    font-style: normal;
    font-size: 8px;
`

export default Testimonials